import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { EnrichmentRunScope, EnrichmentRunState, type ProspectBookCountsDto } from '@signalvote/shared';
import { History, MoreHorizontal, Upload } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { useEnrichmentRun, useProspectListProgress, useProspectLists, useStartEnrichmentRun } from '@/hooks/queries';
import { backgroundWork, NEVER_RUN, uploadInProgress, uploadToResume, workPercent } from '@/lib/fundraising/enrichmentWork';
import { cn } from '@/lib/utils';
import { AddPeopleOverlay, type AddPeopleStep } from './AddPeopleOverlay';
import { EnrichmentPipeline } from './EnrichmentPipeline';
import { PastUploads } from './PastUploads';
import { PROSPECT_LIST_TEMPLATE_URL } from './ProspectListTemplateGuide';

const n = (value: number) => value.toLocaleString();
const people = (count: number) => `${n(count)} ${count === 1 ? 'person' : 'people'}`;

/**
 * Adding people and matching them to federal giving, as the Prospects page shows it: Import people (with
 * Past uploads and Re-match everyone beside it, portalled into the page header's action slot) and the
 * overlay it opens; a progress banner with Open while work goes on behind a closed overlay; a banner when
 * some people haven't been matched yet (or matching didn't finish); and the empty book, which is the upload
 * itself. The overlay opens only on one of those actions, never by itself.
 *
 * <p>Kept apart from the book so the run's and the upload's live updates re-render this, not the whole
 * book behind the overlay (which made it stutter).</p>
 */
export function EnrichmentWork({
  counts,
  searching,
  emptyBook,
  actionSlot,
  onReviewPossible,
  onSeeEveryone,
  onShowUnmatched,
}: {
  /** The book's counts; absent while it loads. They follow the search, like the segments. */
  counts: ProspectBookCountsDto | undefined;
  searching: boolean;
  emptyBook: boolean;
  /** Where the header's actions go; nothing is shown there until it mounts. */
  actionSlot: HTMLElement | null;
  onReviewPossible: () => void;
  onSeeEveryone: () => void;
  /** Narrow the book to the people not matched yet. */
  onShowUnmatched: () => void;
}) {
  const { data: runRead } = useEnrichmentRun();
  const run = runRead ?? NEVER_RUN;
  const { mutate: startRun, error: startError } = useStartEnrichmentRun();
  const { data: lists } = useProspectLists();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState<AddPeopleStep>('file');
  const [droppedFile, setDroppedFile] = useState<File | null>(null);
  const [dragging, setDragging] = useState(false);
  const [confirmRematch, setConfirmRematch] = useState(false);
  const [pastUploadsOpen, setPastUploadsOpen] = useState(false);
  const importButton = useRef<HTMLButtonElement>(null);

  // The upload the overlay follows: the one started here, or, after a reload or coming back to the page,
  // the one still at work (see `uploadToResume`). Held once picked up, so it's still followed while its
  // people are enriched (the list route reads it as finished by then).
  const [listId, setListId] = useState<string | null>(null);
  const resumable = lists ? uploadToResume(lists, run) : undefined;
  useEffect(() => {
    if (!listId && resumable) setListId(resumable.id);
  }, [listId, resumable]);
  const streamed = useProspectListProgress(listId);
  const upload = streamed ?? lists?.find((l) => l.id === listId) ?? null;

  const uploadBusy = !!listId && (!upload || uploadInProgress(upload));
  const runBusy = run.state === EnrichmentRunState.Running;
  const working = uploadBusy || runBusy;

  const openOn = (next: AddPeopleStep) => {
    if (next === 'file') setDroppedFile(null);
    setStep(next);
    setOpen(true);
  };

  const close = () => {
    setOpen(false);
    setDroppedFile(null);
    // A finished upload has told its story; the next Open follows whatever runs next.
    if (!uploadBusy) setListId(null);
  };

  const match = (scope: EnrichmentRunScope) => {
    setListId(null);
    // A start that fails puts the last run back; the overlay mustn't pass its old result off as new.
    startRun(scope, { onError: () => setOpen(false) });
    openOn('watch');
  };

  const leadTo = (next: () => void) => () => {
    close();
    next();
  };

  const unmatched = counts?.notMatchedYet ?? 0;
  const everyone = counts?.everyone ?? 0;
  // The last run stopped, or couldn't answer for some people: what's left is a failure to retry, not a
  // first match to run.
  const didntFinish =
    run.state === EnrichmentRunState.Failed || (run.state === EnrichmentRunState.Completed && (run.summary?.peopleFailed ?? 0) > 0);
  const background = working && !open ? backgroundWork(run, uploadBusy ? upload : null) : null;

  const actions = (
    <>
      <Button variant="ghost" onClick={() => setPastUploadsOpen(true)}>
        <History className="mr-1.5 h-4 w-4" aria-hidden="true" />
        Past uploads
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-10 w-10" aria-label="More actions">
            <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem disabled={working || everyone === 0} onSelect={() => setConfirmRematch(true)}>
            Re-match everyone
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      <Button ref={importButton} className="h-10" onClick={() => openOn('file')}>
        Import people
      </Button>
    </>
  );

  return (
    <>
      {actionSlot && createPortal(actions, actionSlot)}

      {background && (
        <div className="flex items-center gap-4 rounded-xl border bg-card py-3 pl-5 pr-3 text-[13px]">
          <div className="flex min-w-0 flex-1 flex-col gap-2">
            <div role="status" className="min-w-0 space-y-0.5">
              <p className="truncate font-semibold text-foreground">{background.headline}</p>
              {background.counts && (
                <p className="text-xs tabular-nums text-muted-foreground">
                  {[
                    `${n(background.counts.checked)} of ${n(background.counts.total)} checked`,
                    `${n(background.counts.matched)} matched`,
                    `${n(background.counts.toReview)} to review`,
                    `${n(background.counts.noMatch)} no match`,
                    `${n(background.counts.waiting)} waiting`,
                  ].join(' · ')}
                </p>
              )}
            </div>
            <div
              role="progressbar"
              aria-label="Work done"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={workPercent(run, uploadBusy ? upload : null)}
              className="h-1 overflow-hidden rounded-sm bg-border"
            >
              <div
                className="h-full bg-primary transition-[width] duration-500 ease-out"
                style={{ width: `${workPercent(run, uploadBusy ? upload : null)}%` }}
              />
            </div>
            {background.counts && (
              <p className="text-xs text-muted-foreground">
                All {n(background.counts.total)} are already in your book and can go on call books.
              </p>
            )}
          </div>
          <Button variant="outline" onClick={() => openOn('watch')}>
            Open
          </Button>
        </div>
      )}

      {unmatched > 0 && !working && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border bg-card px-5 py-3 text-[13px]">
          <div className="min-w-0 space-y-0.5">
            {didntFinish && <p className="font-semibold text-destructive">Matching didn’t finish</p>}
            <p className={cn(!didntFinish && 'font-semibold')}>
              {people(unmatched)} {unmatched === 1 ? 'hasn’t' : 'haven’t'} been matched yet.
            </p>
            <p className="text-xs text-muted-foreground">
              They stay in your book and can be called, but have no suggested ask until
              they’re matched.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onShowUnmatched}>
              Show these {n(unmatched)}
            </Button>
            <Button variant="outline" onClick={() => match(EnrichmentRunScope.Unmatched)}>
              {didntFinish ? 'Retry' : 'Match them'}
            </Button>
          </div>
        </div>
      )}
      {startError && <p className="text-sm text-warning">{startError.message}</p>}

      {emptyBook && !working && (
        <section aria-labelledby="empty-book-title" className="mx-auto max-w-2xl space-y-6 py-10">
          <div className="space-y-1">
            <h2 id="empty-book-title" className="text-base font-semibold text-foreground">
              Your book is empty
            </h2>
            <p className="text-[13px] text-muted-foreground">
              Add a file of people to call. We check each person against federal giving records while the file loads.
              You can start building call books before that finishes.
            </p>
          </div>
          <div
            className={cn(
              'flex flex-col items-center gap-3 rounded-xl border border-dashed bg-card px-6 py-10 text-center transition-colors',
              dragging && 'border-primary bg-muted',
            )}
            onDragOver={(e) => {
              e.preventDefault();
              setDragging(true);
            }}
            onDragLeave={() => setDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setDragging(false);
              const file = e.dataTransfer.files?.[0];
              if (!file) return;
              setStep('file');
              setDroppedFile(file);
              setOpen(true);
            }}
          >
            <Upload className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
            <p className="text-[13px] font-semibold text-foreground">Drop a CSV file here</p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Button onClick={() => openOn('file')}>Choose file</Button>
              <a href={PROSPECT_LIST_TEMPLATE_URL} download className="text-[13px] underline underline-offset-4">
                Download the template
              </a>
            </div>
          </div>
          <dl className="grid gap-4 text-[13px] sm:grid-cols-3">
            {[
              ['Each row needs', 'A name, plus a ZIP code or state'],
              ['Matching adds', 'Federal giving history and a suggested ask'],
              ['Matching never', 'Removes, merges or hides anyone'],
            ].map(([term, detail]) => (
              <div key={term}>
                <dt className="text-[10px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">{term}</dt>
                <dd className="mt-1">{detail}</dd>
              </div>
            ))}
          </dl>
        </section>
      )}

      <AddPeopleOverlay
        open={open}
        onOpenChange={(next) => (next ? setOpen(true) : close())}
        step={step}
        initialFile={droppedFile}
        onUploadStarted={(id) => {
          setListId(id);
          setStep('watch');
        }}
        onClosed={() => importButton.current?.focus()}
      >
        <EnrichmentPipeline
          run={run}
          following={!!listId}
          upload={upload}
          onClose={close}
          onReviewPossible={leadTo(onReviewPossible)}
          onSeeEveryone={leadTo(onSeeEveryone)}
          onRetryUnmatched={() => match(EnrichmentRunScope.Unmatched)}
        />
      </AddPeopleOverlay>

      <PastUploads open={pastUploadsOpen} onOpenChange={setPastUploadsOpen} lists={lists ?? []} />

      <AlertDialog open={confirmRematch} onOpenChange={setConfirmRematch}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Re-match everyone?</AlertDialogTitle>
            <AlertDialogDescription>
              This matches {searching ? 'everyone in your book' : `all ${people(everyone)} in your book`} to federal
              giving again, from scratch. Use it after new federal data is published; people added since the last
              match are matched as they&apos;re added.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => match(EnrichmentRunScope.Everyone)}>
              {searching ? 'Re-match everyone' : `Re-match ${people(everyone)}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
